import { Ellipsis, Eye } from 'lucide-react';
import { OpportunityRow } from 'bindings/opportunities/OpportunityRow';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownOwners, { UserBlock } from '../../../components_v2/dropdown/DropdownOwners';
import LightDropdown from '../../../components_v2/dropdown/lightDropdown/LightDropdown';
import { Column } from '../../../components_v2/table/Table';
import { DarkGreySidely, GreySidely3 } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { AlertContext, AlertRes } from '../../alert/AlertProvider';
import { ColumnResume } from '../../client-companies/Companies';
import { CompanySearchCell, DateAccessor, EditableContent, TagCell } from '../../client-companies/data/CompanyColumns';
import { BodyHeaderStatus } from '../../client-companies/style/PopupStyle';
import { TableCellButton } from '../../client-companies/style/Style';
import { ColumnHidder } from '../../contact/data/ContactColumns';
import { validateNumberAndEmpty } from '../../globals/FieldFunction';
import { ToolbarImageContainer } from '../../globals/defaultToolbar/style/Style';
import { LoadingStateEnum } from '../../import/model';
import { Owner } from '../../orders/model/Model';
import { TableRow, TableRowStatus, TableRowStatusBackground } from '../../orders/templateOrders/style/Style';
import Restricted from '../../permissions/Restricted';
import { formatCurrency } from '../../reports/utils';
import { OpportunityStatus } from '../model/Model';
import { QueryUpdateOpportunity } from './Data';

export const OPPORTUNITY_HIDDEN_LOCAL_STORAGE = 'opportunity_hidden_columns';
export const OPPORTUNITY_SORT_LOCAL_STORAGE = 'opportunity_sort_columns';

const OPPORTUNITY_COLUMN_CATEGORIES = ['opportunities', 'contact_activity'] as const;

const ALL_OPPORTUNITY_COLUMNS: Array<ColumnResume> = [
	{ name: 'title', excluded: true, fixedLeft: true, categoryIndex: 0 },
	{ name: 'client_company_name', categoryIndex: 0 },
	{ translationKey: 'status', name: 'opportunity_status_id', categoryIndex: 0 },
	{ translationKey: 'owner', name: 'owner_id', categoryIndex: 0 },
	{ name: 'deadline', categoryIndex: 0 },
	{ name: 'amount', categoryIndex: 0 },
	{ name: 'probability', categoryIndex: 0 },
	{ name: 'revenue', categoryIndex: 0 },
	{ translationKey: 'tags', name: 'tag_id', categoryIndex: 0 },
	{ name: 'created_at', categoryIndex: 1 },
	{ name: 'created_by', hidded: true, categoryIndex: 1 },
	{ name: 'updated_at', hidded: true, categoryIndex: 1 },
	{ name: 'updated_by', hidded: true, categoryIndex: 1 },
	{ name: 'quick_actions', excluded: true, categoryIndex: undefined }
];

export const statusDatalist = (statuses: OpportunityStatus[]) => [...statuses]
	.sort((a, b) => a.sequence - b.sequence)
	.map(s => ({ label: s.name, value: s, color: s.status_color_code }));

export const PROBABILITIES = Array.from({ length: 11 }, (_, i) => ({ label: `${i * 10}%`, value: i * 10 }));

// hue 0 (red) at 0% to 120 (green) at 100%
export const ProbabilityBadge = (props: { probability: number | null, inDropdown?: boolean }) => <BodyHeaderStatus
	inDropdown={props.inDropdown}
	backgroundColor={props.probability === null ? undefined : `hsl(${props.probability * 1.2}, 65%, 45%)`}
>
	{props.probability === null ? '-' : `${props.probability}%`}
</BodyHeaderStatus>;

export default function OpportunityColumns(
	users: Owner[],
	onOpportunityOpen: (id: number) => void,
	onCompanyClick: (id: number) => void,
	updateOpportunity: (id: number, params: QueryUpdateOpportunity) => Promise<void>,
	onDelete: (row: OpportunityRow) => void,
	loadingState: LoadingStateEnum,
	statuses: OpportunityStatus[],
	tags: ITag[],
	updateTags: (id: number, body: { ids_delete?: number[], ids_add?: number[] }) => Promise<void>
): Array<Column<OpportunityRow>> {
	const editable = (row: OpportunityRow, child: React.ReactElement, fallback: React.ReactNode) =>
		<Restricted to={{ objectAction: 'UpdateOpportunity' }} ownerId={row.owner_id} fallback={fallback}>{child}</Restricted>;

	return [
		{
			id: 'title',
			Header: translateToString('title'),
			accessor: row => <TableRow fontWeight="500" color={DarkGreySidely} cursor="pointer" onClick={() => onOpportunityOpen(row.id)}>
				{row.title}
			</TableRow>,
			hoverImage: row => <TableCellButton onClick={() => onOpportunityOpen(row.id)} icon={Eye} />,
			width: 220,
			minWidth: 150,
			freeze: 'left',
			type: 'string',
			toolTip: row => row.title
		},
		{
			id: 'client_company_name',
			Header: translateToString('client_company_name'),
			accessor: row => editable(
				row,
				<CompanySearchCell
					defaultValue={{ name: row.client_company_name ?? undefined, id: row.client_company_id }}
					onUpdateCompany={value => updateOpportunity(row.id, { client_company_id: value.id, contact_id: null })}
				/>,
				<TableRow>{row.client_company_name}</TableRow>
			),
			hoverImage: row => <TableCellButton onClick={() => onCompanyClick(row.client_company_id)} icon={Eye} />,
			width: 200,
			minWidth: 150,
			type: 'string',
			toolTip: row => row.client_company_name
		},
		{
			id: 'opportunity_status_id',
			Header: translateToString('status'),
			accessor: row => {
				const status = statuses.find(s => s.id === row.opportunity_status_id);
				const badge = (onClick?: React.MouseEventHandler<HTMLDivElement>) => <TableRowStatusBackground onClick={onClick} noPointer={!onClick}>
					<TableRowStatus backgroundColor={status?.status_color_code}>{status?.name}</TableRowStatus>
				</TableRowStatusBackground>;
				return editable(
					row,
					<LightDropdown
						dropdownStyle={{ containerHeight: 'unset', optionWidth: '200px' }}
						datalist={statusDatalist(statuses)}
						JSXButton={({ onClick }) => badge(onClick)}
						JSXContent={({ value, onClick }) => <div style={{ margin: 5 }}><TableRowStatusBackground onClick={onClick}><TableRowStatus backgroundColor={value.color}>{value.label}</TableRowStatus></TableRowStatusBackground></div>}
						// as in the creation popup, picking a status applies its probability
						onChange={value => value.value && updateOpportunity(row.id, { opportunity_status_id: value.value.id, probability: value.value.probability })}
					/>,
					badge()
				);
			},
			width: 150,
			minWidth: 150,
			type: 'status',
		},
		{
			id: 'owner_id',
			Header: translateToString('owner'),
			accessor: row => {
				const user = users.find(u => u.id == row.owner_id);
				return editable(
					row,
					<DropdownOwners
						dropdownStyle={{
							containerWidth: 'initial',
							containerJustifyContent: 'flex-start',
							optionWidth: '200px',
							optionHeight: '150px',
							optionPadding: '5px 10px 5px 10px',
							optionLeft: '-10px',
							width: '180px',
							backgroundColor: 'none',
							fontSize: 12
						}}
						users={users.map(u => ({ label: u.name, value: u, image: u.photoUrl }))}
						selected={user ? { label: user.name, value: user, image: user.photoUrl } : undefined}
						onChange={value => value.value && updateOpportunity(row.id, { owner_id: value.value.id })}
					/>,
					<UserBlock user={user} />
				);
			},
			width: 200,
			minWidth: 200,
			type: 'user',
		},
		{
			id: 'deadline',
			Header: translateToString('deadline'),
			accessor: row => <DateAccessor date={row.deadline} />,
			width: 150,
			minWidth: 150,
			type: 'temporal',
		},
		{
			id: 'amount',
			Header: translateToString('amount'),
			accessor: row => editable(
				row,
				<EditableContent<OpportunityRow>
					innerId={`${row.id}-${loadingState}`}
					row={row}
					valueKey='amount'
					regex={validateNumberAndEmpty}
					onValueUpdate={value => updateOpportunity(row.id, { amount: value ? Number(value) : null })}
				/>,
				<TableRow>{formatCurrency(row.amount)}</TableRow>
			),
			width: 130,
			minWidth: 100,
			editable: true,
			type: 'numeric',
		},
		{
			id: 'probability',
			Header: translateToString('probability'),
			accessor: row => editable(
				row,
				<LightDropdown
					dropdownStyle={{ containerHeight: 'unset', optionWidth: '100px' }}
					datalist={PROBABILITIES}
					JSXButton={({ onClick }) => <TableRowStatusBackground onClick={onClick}><ProbabilityBadge probability={row.probability} /></TableRowStatusBackground>}
					JSXContent={({ value, onClick }) => <div style={{ margin: 5 }}><TableRowStatusBackground onClick={onClick}><ProbabilityBadge probability={value.value} /></TableRowStatusBackground></div>}
					onChange={value => value.value !== undefined && updateOpportunity(row.id, { probability: value.value })}
				/>,
				<TableRowStatusBackground noPointer><ProbabilityBadge probability={row.probability} /></TableRowStatusBackground>
			),
			width: 110,
			minWidth: 100,
			type: 'numeric',
		},
		{
			id: 'revenue',
			Header: translateToString('revenue'),
			accessor: row => <TableRow>{formatCurrency(row.revenue)}</TableRow>,
			width: 130,
			minWidth: 100,
			type: 'numeric',
		},
		{
			id: 'tag_id',
			Header: translateToString('tags'),
			accessor: row => <TagCell
				row={{ ...row, tags: tags.filter(t => row.tag_ids.includes(t.id!)) }}
				tags={tags}
				onChange={v => updateTags(row.id, { ids_add: v.filter(t => !row.tag_ids.includes(t.id!)).map(t => t.id!) })}
				onDelete={v => updateTags(row.id, { ids_delete: [v] })}
			/>,
			width: 250,
			minWidth: 250,
			type: 'tag',
			disableSortBy: true,
		},
		{
			id: 'created_at',
			Header: translateToString('created_at'),
			accessor: row => <DateAccessor date={row.created_at} />,
			width: 150,
			minWidth: 150,
			type: 'temporal',
		},
		{
			id: 'created_by',
			Header: translateToString('created_by'),
			accessor: row => <UserBlock user={users.find(u => u.id == row.created_by)} />,
			width: 200,
			minWidth: 150,
			type: 'user'
		},
		{
			id: 'updated_at',
			Header: translateToString('updated_at'),
			accessor: row => <DateAccessor date={row.updated_at} />,
			width: 150,
			minWidth: 150,
			type: 'temporal',
		},
		{
			id: 'updated_by',
			Header: translateToString('updated_by'),
			accessor: row => <UserBlock user={users.find(u => u.id == row.updated_by)} />,
			width: 200,
			minWidth: 150,
			type: 'user',
		},
		{
			id: 'quick_actions',
			noHeaderEllipsis: true,
			Header: <ColumnHidder
				categories={OPPORTUNITY_COLUMN_CATEGORIES}
				columns={ALL_OPPORTUNITY_COLUMNS}
				hiddenColumnKey={OPPORTUNITY_HIDDEN_LOCAL_STORAGE}
				sortColumnKey={OPPORTUNITY_SORT_LOCAL_STORAGE}
			/>,
			accessor: row => <Restricted to={{ objectAction: 'DeleteOpportunity' }} ownerId={row.owner_id}>
				<DeleteOption row={row} onDelete={onDelete} />
			</Restricted>,
			width: 40,
			minWidth: 40,
			disableSortBy: true,
			disableFilter: true,
			unresizeable: true,
			freeze: 'right'
		}
	];
}

function DeleteOption(props: { row: OpportunityRow, onDelete: (row: OpportunityRow) => void }): JSX.Element {
	const { alertDelete } = React.useContext(AlertContext);
	return <Dropdown
		searchable={false}
		name='quick_action'
		datalist={[{ label: translateToString('global.quick_action.delete'), value: 'DELETE' }]}
		readOnly
		dropdownStyle={{ width: '10px', optionWidth: '100px', fontSize: 13, optionLeft: '-112px', containerTop: '-6px' }}
		JSXButton={() => <ToolbarImageContainer rotate={'90deg'} marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} size={24} /></ToolbarImageContainer>}
		onChange={() => alertDelete({ zIndex: 201, name: props.row.title ?? '' })
			.then(result => result.res === AlertRes.Ok && props.onDelete(props.row))}
	/>;
}
